import { useState, useEffect } from "react";

import toast from "react-hot-toast";

import { DomainCard } from "@/components/custom-domains/DomainCard";

import {
  customDomainsApi,
  CustomDomain,
  EntriConfig,
} from "@/lib/api/custom-domains";
import { toEntriRecords } from "@/lib/entri";
import { useEntri } from "@/hooks/use-entri";
import { analytics } from "@/lib/analytics";
import { ENTRI_EVENTS } from "@/components/custom-domains/entri-events";

interface CustomDomainConnectHITLProps {
  /** The custom domain the agent just added (from the HITL payload). */
  domain: CustomDomain;
  projectId: string;
}

/**
 * Inline agent HITL card for connecting a custom domain — renders the same
 * `DomainCard` the Custom Domains modal uses, so the user gets the one-click
 * Entri connect + manual DNS fallback + verify/set-primary right in chat.
 * Self-contained: it owns the single domain's state and calls `customDomainsApi`
 * directly (the chat runs as the authenticated user, like the modal).
 */
export function CustomDomainConnectHITL({
  domain: initialDomain,
  projectId,
}: CustomDomainConnectHITLProps) {
  const [domain, setDomain] = useState<CustomDomain>(initialDomain);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isAutoChecking, setIsAutoChecking] = useState(false);
  // Connected via Entri (DNS written for the user) — hides the manual "Stop".
  const [viaEntri, setViaEntri] = useState(false);
  const [isSettingPrimary, setIsSettingPrimary] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [entriConfig, setEntriConfig] = useState<EntriConfig | null>(null);

  // Entri one-click connect. On success, kick verify + auto-poll (same as modal).
  const { open: openEntri } = useEntri({
    onSuccess: () => {
      analytics.track(ENTRI_EVENTS.CONNECT_SUCCEEDED, {
        projectId,
        domain: domain.domain,
      });
      toast.success("DNS configured! Verifying your domain…");
      setIsAutoChecking(true);
      setViaEntri(true);
      handleVerify();
    },
  });

  // Refresh the domain's current state on mount — the payload is a snapshot from
  // when it was added; after a page reload it may already be verified/active (or
  // removed). Keeps a persisted card accurate.
  useEffect(() => {
    let cancelled = false;
    customDomainsApi
      .list(projectId)
      .then((domains) => {
        if (cancelled) return;
        const latest = domains.find((d) => d.id === initialDomain.id);
        if (latest) setDomain(latest);
        else setDeleted(true);
      })
      .catch(() => {
        // keep the snapshot; user can verify manually
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, initialDomain.id]);

  // Fetch Entri config so the card can offer one-click connect (greenfield-safe).
  useEffect(() => {
    let cancelled = false;
    customDomainsApi
      .getEntriConfig(projectId)
      .then((config) => {
        if (!cancelled) setEntriConfig(config);
      })
      .catch(() => {
        if (!cancelled) setEntriConfig({ enabled: false });
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Auto-poll while verifying, or while the hostname is active but SSL is still
  // provisioning — until BOTH are active. (Mirrors the modal's poll.)
  useEffect(() => {
    const provisioning =
      domain.status === "active" && domain.sslStatus !== "active";
    if (!isAutoChecking && !provisioning) return;

    const interval = setInterval(async () => {
      try {
        const updated = await customDomainsApi.verify(projectId, domain.id);
        setDomain(updated);
        if (updated.status === "active" && updated.sslStatus === "active") {
          setIsAutoChecking(false);
          toast.success(`${updated.domain} verified! 🎉`);
        }
      } catch {
        // keep polling; user can verify manually
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [isAutoChecking, domain.status, domain.sslStatus, domain.id, projectId]);

  const entriEnabled = entriConfig?.enabled === true;

  const handleVerify = async (): Promise<void> => {
    setIsVerifying(true);
    try {
      const updated = await customDomainsApi.verify(projectId, domain.id);
      setDomain(updated);
      if (updated.status === "active" && updated.sslStatus === "active") {
        toast.success("Domain verified successfully!");
        setIsAutoChecking(false);
      } else if (updated.status === "failed") {
        toast.error("Verification failed. Check DNS records.");
        setIsAutoChecking(false);
      } else {
        setIsAutoChecking(true);
        toast("Checking DNS records... We'll notify you when ready.");
      }
    } catch {
      toast.error("Failed to verify domain");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSetPrimary = async (): Promise<void> => {
    setIsSettingPrimary(true);
    try {
      await customDomainsApi.setPrimary(projectId, domain.id);
      setDomain((d) => ({ ...d, isPrimary: true }));
      toast.success(
        "Primary domain set! Your project is now live on this domain.",
      );
    } catch (error: any) {
      toast.error(error.message || "Failed to set primary domain");
    } finally {
      setIsSettingPrimary(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    setIsDeleting(true);
    try {
      await customDomainsApi.delete(projectId, domain.id);
      setDeleted(true);
      toast.success("Domain removed");
    } catch {
      toast.error("Failed to remove domain");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConnectEntri = async (): Promise<void> => {
    if (
      !entriConfig?.enabled ||
      !entriConfig.applicationId ||
      !entriConfig.token
    ) {
      throw new Error("Entri is not available");
    }
    analytics.track(ENTRI_EVENTS.CONNECT_OPENED, {
      projectId,
      domain: domain.domain,
    });
    await openEntri({
      applicationId: entriConfig.applicationId,
      token: entriConfig.token,
      prefilledDomain: domain.domain,
      dnsRecords: toEntriRecords(domain.domain, domain.dnsRecords),
      userId: domain.id, // echoed back in Entri webhooks for correlation
    });
  };

  if (deleted) {
    return (
      <div className="rounded-xl border-[0.5px] border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
        Removed <span className="font-mono">{domain.domain}</span> from this
        project.
      </div>
    );
  }

  return (
    <DomainCard
      domain={domain}
      isAutoChecking={isAutoChecking}
      viaEntri={viaEntri}
      isVerifying={isVerifying}
      isDeleting={isDeleting}
      isSettingPrimary={isSettingPrimary}
      entriEnabled={entriEnabled}
      onConnectEntri={handleConnectEntri}
      onVerify={handleVerify}
      onStopAutoCheck={() => {
        setIsAutoChecking(false);
        toast("Auto-check stopped. You can verify manually anytime.");
      }}
      onSetPrimary={handleSetPrimary}
      onDelete={handleDelete}
    />
  );
}
